/* OMNIMUSCLE Premium gate
   Modal shown when a free user taps a Premium-locked feature.
   Server-side `user.is_premium` (via AuthContext) is the source of truth.
   localStorage flag remains as a write-side helper synced by AuthContext for
   backward compatibility with components that still call `usePremium()`.
   Exposes:
   - usePremium() — true when server says user is premium OR owner OR (legacy) localStorage=true
   - getPremium() / setPremium() — legacy helpers (still used by AuthContext to sync)
   - PremiumBadge / LockIcon / PremiumGate (default)
*/
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Sparkles, Check, X, Crown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const KEY = "omnimuscle_premium_v1";

export function getPremium() {
  try {
    return localStorage.getItem(KEY) === "true";
  } catch {
    return false;
  }
}

export function setPremium(value) {
  try {
    if (value) localStorage.setItem(KEY, "true");
    else localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
  window.dispatchEvent(new Event("premium-changed"));
}

// Hook reads from BOTH the AuthContext user (server truth) and the localStorage
// flag. Server wins when present — this means devtools can't grant premium by
// flipping localStorage when a logged-out user has no server flag.
export function usePremium() {
  const { user } = useAuth();
  const [localFlag, setLocalFlag] = useState(getPremium());
  useEffect(() => {
    const onChange = () => setLocalFlag(getPremium());
    window.addEventListener("premium-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("premium-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);
  // Server is source of truth when logged in.
  if (user && user !== false) {
    return Boolean(user.is_premium) || user.role === "owner";
  }
  // Logged out (or AuthContext still resolving) — fall back to the local flag.
  return localFlag;
}

export function PremiumBadge({ size = "sm", testId }) {
  const sizeClass =
    size === "lg"
      ? "text-xs px-3 py-1.5"
      : "text-[10px] px-2 py-0.5";
  return (
    <span
      data-testid={testId || "premium-badge"}
      className={`inline-flex items-center gap-1 ${sizeClass} font-bold uppercase tracking-widest bg-[#D4AF37] text-black`}
    >
      <Sparkles size={10} />
      Premium
    </span>
  );
}

export function LockIcon() {
  return (
    <span className="inline-flex items-center justify-center w-6 h-6 bg-[#D4AF37] text-black flex-shrink-0">
      <Lock size={12} strokeWidth={2.5} />
    </span>
  );
}

const PERKS = [
  "Unlimited AI Coach plans",
  "Barcode scanner for the Food Log",
  "Macro Master targets + TDEE memory",
  "Inner Circle premium badge \ud83e\udd47",
  "+1000 Phoenix Token welcome bonus \ud83d\udd25",
  "Priority placement in the Hall of Fame",
];

export default function PremiumGate({
  open,
  onClose,
  feature = "this feature",
}) {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (!open) return null;

  const trialUsed = Boolean(user && user.trial_used);
  const goToPlans = (which) => {
    onClose?.();
    navigate(`/plans${which ? `?focus=${which}` : ""}`);
  };

  return (
    <div
      data-testid="premium-gate"
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-lg w-full p-6 my-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="premium-gate-close"
          onClick={onClose}
          className="absolute top-4 right-4 text-neutral-500 hover:text-white"
          aria-label="Close"
        >
          <X size={22} />
        </button>

        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-[#D4AF37] flex items-center justify-center">
            <Lock size={22} className="text-black" />
          </div>
          <PremiumBadge size="lg" />
        </div>

        <h3 className="font-display text-3xl text-white mb-2 leading-tight">
          {feature} is a Premium perk.
        </h3>
        <p className="text-neutral-400 text-sm mb-5 leading-relaxed">
          Join the OMNIMUSCLE Inner Circle — unlock the full athlete toolkit.
        </p>

        <ul className="space-y-2 mb-5">
          {PERKS.map((p) => (
            <li
              key={p}
              className="flex items-start gap-2.5 text-sm text-neutral-200"
            >
              <Check
                size={16}
                className="text-[#D4AF37] mt-0.5 flex-shrink-0"
              />
              {p}
            </li>
          ))}
        </ul>

        {/* Trial card — disabled if already used */}
        <button
          data-testid="gate-trial"
          onClick={() => !trialUsed && goToPlans("trial")}
          disabled={trialUsed}
          className={`w-full mb-3 p-3 border text-left flex items-center justify-between transition ${
            trialUsed
              ? "bg-[#0A0A0A] border-zinc-800 cursor-not-allowed opacity-60"
              : "bg-[#1a0d2b] border-purple-500/60 hover:border-purple-400"
          }`}
        >
          <div>
            <div className="text-[10px] uppercase tracking-widest text-purple-400 mb-0.5">
              {trialUsed ? "Trial Used" : "7-Day Trial"}
            </div>
            <div className="font-display text-2xl text-white">
              $0.99{" "}
              <span className="text-xs text-neutral-500 font-body normal-case tracking-normal">
                / 7 days
              </span>
            </div>
          </div>
          {!trialUsed && (
            <span className="text-purple-400 text-xs font-bold uppercase tracking-widest">
              Start \u2192
            </span>
          )}
        </button>

        {/* Monthly + Yearly side by side */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <button
            data-testid="gate-monthly"
            onClick={() => goToPlans("monthly")}
            className="bg-[#0A0A0A] border-2 border-[#D4AF37] p-3 text-center relative hover:bg-[#D4AF37]/5 transition"
          >
            <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black text-[9px] font-bold uppercase tracking-widest px-2 py-0.5">
              Most Popular
            </span>
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">
              Monthly
            </div>
            <div className="font-display text-3xl text-[#D4AF37]">$18.99</div>
            <div className="text-[10px] text-neutral-500">/ 30 days</div>
          </button>
          <button
            data-testid="gate-annual"
            onClick={() => goToPlans("yearly")}
            className="bg-[#0A0A0A] border border-red-700/70 p-3 text-center relative hover:border-red-500 transition"
          >
            <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-red-700 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5">
              Save ~17%
            </span>
            <div className="text-[10px] uppercase tracking-widest text-red-400">
              Yearly
            </div>
            <div className="font-display text-3xl text-white">$189</div>
            <div className="text-[10px] text-neutral-500">/ 365 days</div>
          </button>
        </div>

        <button
          data-testid="gate-cta-plans"
          onClick={() => goToPlans()}
          className="btn-omni btn-red w-full mb-1"
        >
          <Crown size={14} />
          See All Premium Plans
        </button>
        <p className="text-[10px] text-neutral-600 text-center mt-3">
          Secure checkout by Stripe \u00b7 Cancel anytime
        </p>
      </div>
    </div>
  );
}
